import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil, tap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

import {
  AuthStore,
  DevicesStore,
  RichDevice,
  InstallationDetailsStore,
  isSolarLogInverter,
  OwnerDetails,
  RemoteServiceStore,
  RemoteServicePriority,
} from '@store';

import { ActivatedRoute } from '@angular/router';
import { EditSerialNumberModalComponent } from '../../../../shared/components/edit-serial-number-modal/edit-serial-number-modal.component';
import { NO_VALUE } from '../../../../shared/pipes';
import { matTooltipSetter } from '../../../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { PillType } from '../../../../shared/components/pill/pill.model';
import { DiagnosisData } from '@vi-iot/client/index';
import { BrandLogo } from '../../../helpers/brand-logo';

@Component({
  selector: 'app-common-installation-tile',
  templateUrl: './common-installation-tile.component.html',
  styleUrls: ['./common-installation-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommonInstallationTileComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  @Input() device: RichDevice;

  readonly NO_VALUE = NO_VALUE;
  readonly PillType = PillType;

  deviceName: string;
  gatewaySerial: string;
  gatewayHasDevices: boolean;
  serial: string;
  owner: OwnerDetails;
  imageUri: string;
  isAdminOrSupportInternational$: Observable<boolean>;
  isPermanentInspectionEntriesVisible$: Observable<boolean>;
  isPermanentInspectionMenuItemVisible$: Observable<boolean>;
  anomalyListForDevice$: Observable<DiagnosisData[]>;
  brandLogo: string;
  anomalyPillType: PillType;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public devicesStore: DevicesStore,
    public authStore: AuthStore,
    public remoteServiceStore: RemoteServiceStore,
    private translation: TranslateService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.device.currentValue?.shortDeviceName !== changes.device.previousValue?.shortDeviceName
    ) {
      this.deviceName = this.getDeviceName(this.device);
    }
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.gatewaySerial = params?.gatewaySerial;
      this.isPermanentInspectionEntriesVisible$ = this.installationDetailsStore.isPermanentInspectionEntriesVisible(
        this.gatewaySerial
      );
    });

    this.isAdminOrSupportInternational$ = this.authStore.isAdminOrSupportInternational();
    this.installationDetailsStore.owner$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((ownerDetails) => {
        this.owner = ownerDetails;
      });

    this.installationDetailsStore
      .selectGatewayHasDevices(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((gatewayHasDevices) => {
        this.gatewayHasDevices = gatewayHasDevices;
      });

    this.imageUri = this.getImageUri(this.device);
    this.serial = this.getSerial(this.device);
    this.deviceName = this.getDeviceName(this.device);
    this.isPermanentInspectionMenuItemVisible$ = this.installationDetailsStore.isPermanentInspectionMenuItemVisible();
    this.anomalyListForDevice$ = this.devicesStore
      .selectAnomalyListForDevice(this.device.boilerSerial)
      .pipe(
        tap((anomalies) => {
          const isInfoAnomalyOnlny = anomalies?.every(
            (anomaly) => anomaly.diagnosis_priority === RemoteServicePriority.Information
          );
          this.anomalyPillType = isInfoAnomalyOnlny
            ? PillType.infoInverted
            : PillType.mediumInverted;
        })
      );
    const deviceRoles = this.device?.roles;
    this.brandLogo = BrandLogo.getBrandLogo(deviceRoles);
  }

  openBoilerSerialModal(boilerSerial: string): void {
    this.dialog.open(EditSerialNumberModalComponent, {
      width: '496px',
      panelClass: 'edit-serial-number-dialog',
      disableClose: true,
      data: {
        serialNumber: boilerSerial,
        editBoilerSerial: true,
      },
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private getImageUri({ isStandalone }: RichDevice): string {
    return isStandalone || !this.gatewayHasDevices ? '/assets/img/devices/standalone.png' : '';
  }

  private getDeviceName({ isStandalone, shortDeviceName }: RichDevice): string {
    if (!this.gatewayHasDevices) {
      return this.translation.instant('+INSTALLATIONS.INSTALLATION.GATEWAY.TITLE');
    } else if (isStandalone) {
      return this.translation.instant(
        '+INSTALLATIONS.INSTALLATION.BASIC_CONTROL.COMMISSIONING_MANAGER.TITLE'
      );
    } else if (shortDeviceName) {
      return shortDeviceName;
    } else {
      const heaterOf = this.translation.instant('+INSTALLATIONS.INSTALLATIONS.INSTALLATION_OF');

      const ownerName = this.owner?.name ?? '';

      return `${heaterOf} ${ownerName}`;
    }
  }

  private getSerial({ isStandalone, gatewaySerial, boilerSerial }: RichDevice): string {
    return isStandalone || !this.gatewayHasDevices ? gatewaySerial : boilerSerial;
  }

  protected shouldDisplayDeviceImage(): boolean {
    return !isSolarLogInverter(this.device);
  }
}
